import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import { LineChart, BarChart, PieChart } from "@mui/x-charts";
import Icon from "@mdi/react";
import {
	mdiAccountGroup,
	mdiBriefcaseCheckOutline,
	mdiClipboardListOutline,
	mdiCounter,
	mdiRefresh,
	mdiServerNetwork,
	mdiAlertOutline,
} from "@mdi/js";
import { BackofficeAdminServices, BackofficeDashboardDTO, NotistackServices } from "shared/services";
import { HandleError } from "shared/utils";
import styles from "./styles.module.scss";

const REFRESH_INTERVAL_MS = 30_000;

const fmtNumber = (n: number): string => new Intl.NumberFormat("pt-BR").format(n);
const fmtTokens = (n: number): string => {
	if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
	if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
	return String(n);
};
const fmtDate = (iso: string | null): string => {
	if (!iso) return "—";
	return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
};

// Google MCPs (gmail/calendar/drive) descontinuados em 2026-06-08; substituidos
// pelos equivalentes Microsoft 365 (outlook/mscalendar/onedrive/teams).
const MCP_COLORS: Record<string, string> = {
	// gmail: "#ea4335",   // descontinuado — usar `outlook`
	// calendar: "#4285f4", // descontinuado — usar `mscalendar`
	// drive: "#34a853",    // descontinuado — usar `onedrive`
	outlook: "#0078d4",
	mscalendar: "#0078d4",
	onedrive: "#0078d4",
	teams: "#6264a7",
	notion: "#000000",
	slack: "#4a154b",
	meta: "#0467df",
	linkedin: "#0a66c2",
	stripe: "#635bff",
	canva: "#00c4cc",
	adobe: "#ff0000",
	trello: "#0079bf",
	ffmpeg: "#007808",
	youtube: "#ff0000",
};

const HEALTH_LABEL: Record<string, string> = {
	ok: "Saudável",
	warning: "Atenção",
	degraded: "Degradada",
};

interface KpiCardProps {
	icon: string;
	iconClass: string;
	label: string;
	value: string;
	hint: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ icon, iconClass, label, value, hint }) => (
	<div className={styles.kpiCard}>
		<div className={clsx(styles.kpiIcon, iconClass)}>
			<Icon path={icon} size={0.9} />
		</div>
		<div className={styles.kpiBody}>
			<span className={styles.kpiLabel}>{label}</span>
			<span className={styles.kpiValue}>{value}</span>
			<span className={styles.kpiHint}>{hint}</span>
		</div>
	</div>
);

export const HomePage: React.FC = () => {
	const [data, setData] = useState<BackofficeDashboardDTO | null>(null);
	const [loading, setLoading] = useState(false);
	const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
	const fetchSeq = useRef(0);

	const reload = useCallback(async (silent: boolean = false) => {
		const seq = ++fetchSeq.current;
		if (!silent) setLoading(true);
		try {
			const result = await BackofficeAdminServices.getDashboard();
			if (fetchSeq.current === seq) {
				setData(result);
				setLastUpdate(new Date());
			}
		} catch (err) {
			if (!silent) {
				NotistackServices.showErrorMessage(
					HandleError.isHandleError(err) ? err.message : "Erro ao carregar dashboard",
				);
			}
		} finally {
			if (fetchSeq.current === seq && !silent) setLoading(false);
		}
	}, []);

	useEffect(() => {
		void reload();
		const id = setInterval(() => void reload(true), REFRESH_INTERVAL_MS);
		return () => clearInterval(id);
	}, [reload]);

	const charts = data?.charts;
	const tokensByDayData = useMemo(() => {
		if (!charts?.tokensByDay) return { x: [] as string[], y: [] as number[] };
		return {
			x: charts.tokensByDay.map((d) => d.date.slice(5)), // MM-DD
			y: charts.tokensByDay.map((d) => d.tokens),
		};
	}, [charts?.tokensByDay]);

	if (!data && loading) {
		return (
			<div className={styles.page}>
				<div className={styles.loadingState}>
					<CircularProgress size={28} />
					<span>Carregando dashboard...</span>
				</div>
			</div>
		);
	}
	if (!data) return null;

	const { kpis, activity } = data;
	const totalQueueFailed = activity.systemHealth.queues.reduce(
		(acc, q) => acc + (q.counts.failed ?? 0),
		0,
	);
	const overallHealth: "ok" | "warning" | "degraded" =
		activity.systemHealth.queues.some((q) => q.healthStatus === "degraded")
			? "degraded"
			: activity.systemHealth.queues.some((q) => q.healthStatus === "warning")
				? "warning"
				: "ok";

	return (
		<div className={styles.page}>
			<div className={styles.header}>
				<div>
					<h1 className={styles.title}>Dashboard</h1>
					<p className={styles.subtitle}>
						Visão geral da plataforma — atualiza automaticamente a cada 30 segundos.
					</p>
				</div>
				<div className={styles.headerRight}>
					<span className={styles.lastUpdate}>
						Última atualização: {lastUpdate ? lastUpdate.toLocaleTimeString("pt-BR") : "—"}
					</span>
					<Tooltip title="Recarregar agora">
						<IconButton onClick={() => reload(false)} disabled={loading}>
							{loading ? <CircularProgress size={18} /> : <Icon path={mdiRefresh} size={0.8} />}
						</IconButton>
					</Tooltip>
				</div>
			</div>

			{/* ─── KPIs ─────────────────────────────────────────── */}
			<div className={styles.kpiGrid}>
				<KpiCard
					icon={mdiBriefcaseCheckOutline}
					iconClass={styles.kpiIconBlue}
					label="Workspaces / Equipes ativas"
					value={`${fmtNumber(kpis.workspaces.active)} / ${fmtNumber(kpis.teams.active)}`}
					hint={`${fmtNumber(kpis.templates.active)} templates ativos`}
				/>
				<KpiCard
					icon={mdiAccountGroup}
					iconClass={styles.kpiIconGreen}
					label="Membros IA contratados"
					value={fmtNumber(kpis.members.total)}
					hint={`Jr ${kpis.members.junior} · Pl ${kpis.members.pleno} · Sr ${kpis.members.senior}`}
				/>
				<KpiCard
					icon={mdiClipboardListOutline}
					iconClass={styles.kpiIconAmber}
					label="Tasks (últimos 7 dias)"
					value={fmtNumber(kpis.tasks.last7d)}
					hint={`Hoje ${kpis.tasks.today} · Total acum. ${fmtNumber(kpis.tasks.total)}`}
				/>
				<KpiCard
					icon={mdiCounter}
					iconClass={styles.kpiIconRose}
					label="Tokens consumidos (mês)"
					value={fmtTokens(kpis.tokens.thisMonth)}
					hint={`Hoje ${fmtTokens(kpis.tokens.today)} · Total acum. ${fmtTokens(kpis.tokens.total)}`}
				/>
			</div>

			{/* ─── Charts ─────────────────────────────────────── */}
			<div className={styles.chartsRow}>
				<div className={clsx(styles.chartCard, styles.bigChart)}>
					<h2 className={styles.chartTitle}>Tokens consumidos por dia</h2>
					<p className={styles.chartSubtitle}>últimos 14 dias</p>
					{tokensByDayData.x.length > 0 ? (
						<LineChart
							xAxis={[{ data: tokensByDayData.x, scaleType: "band" }]}
							series={[{ data: tokensByDayData.y, label: "Tokens", color: "#6366f1", area: true }]}
							height={260}
							margin={{ left: 60, right: 20, top: 20, bottom: 30 }}
						/>
					) : (
						<div className={styles.empty}>Sem dados de tokens nos últimos 14 dias.</div>
					)}
				</div>

				<div className={styles.chartCard}>
					<h2 className={styles.chartTitle}>Distribuição por MCP</h2>
					<p className={styles.chartSubtitle}>chamadas registradas (deliverables)</p>
					{charts && charts.mcpUsage.length > 0 ? (
						<PieChart
							series={[
								{
									data: charts.mcpUsage.map((m, i) => ({
										id: m.mcpSlug,
										value: m.count,
										label: m.mcpSlug,
										color: MCP_COLORS[m.mcpSlug] ?? `hsl(${(i * 67) % 360}, 60%, 50%)`,
									})),
									innerRadius: 40,
									outerRadius: 90,
									paddingAngle: 2,
								},
							]}
							height={260}
							margin={{ left: 0, right: 90, top: 20, bottom: 20 }}
						/>
					) : (
						<div className={styles.empty}>Sem chamadas MCP registradas ainda.</div>
					)}
				</div>
			</div>

			<div className={styles.chartsRow}>
				<div className={styles.chartCard}>
					<h2 className={styles.chartTitle}>Top 5 membros por consumo</h2>
					<p className={styles.chartSubtitle}>tokens usados acumulados</p>
					{charts && charts.topMembers.length > 0 ? (
						<BarChart
							dataset={charts.topMembers
								.slice()
								.reverse()
								.map((m) => ({ name: m.memberName, tokens: m.tokensUsed }))}
							yAxis={[{ scaleType: "band", dataKey: "name" }]}
							series={[{ dataKey: "tokens", label: "Tokens usados", color: "#22c55e" }]}
							layout="horizontal"
							height={260}
							margin={{ left: 180, right: 20, top: 20, bottom: 30 }}
						/>
					) : (
						<div className={styles.empty}>Sem consumo registrado.</div>
					)}
				</div>

				<div className={styles.chartCard}>
					<h2 className={styles.chartTitle}>Status do sistema</h2>
					<p className={styles.chartSubtitle}>
						<span className={clsx(styles.healthDot, styles[`health_${overallHealth}` as const])} />
						{HEALTH_LABEL[overallHealth]} · {totalQueueFailed} jobs falhos
					</p>
					<div>
						{activity.systemHealth.queues.map((q) => (
							<div key={q.name} className={styles.queueRow}>
								<div>
									<span className={clsx(styles.healthDot, styles[`health_${q.healthStatus}` as const])} />
									{q.name.replace(/^ekeeps-[^-]+-/, "")}
								</div>
								<div className={styles.queueCounts}>
									<span>active: {q.counts.active ?? 0}</span>
									<span>waiting: {q.counts.waiting ?? 0}</span>
									<span>delayed: {q.counts.delayed ?? 0}</span>
									<span>failed: {q.counts.failed ?? 0}</span>
								</div>
							</div>
						))}
						<div
							className={styles.queueRow}
							style={{ marginTop: 12, borderTop: "1px solid var(--mui-palette-divider)", paddingTop: 10 }}
						>
							<div>
								<Icon path={mdiServerNetwork} size={0.55} style={{ verticalAlign: "middle", marginRight: 6 }} />
								Scheduler
							</div>
							<div className={styles.queueCounts}>
								<span>{activity.systemHealth.scheduler.activeScheduled} ativo(s)</span>
								<span>próximo: {fmtDate(activity.systemHealth.scheduler.nextDueAt)}</span>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* ─── Listas de atividade ────────────────────────── */}
			<div className={styles.activityRow}>
				<div className={styles.tableCard}>
					<h2 className={styles.tableTitle}>Últimas tasks executadas</h2>
					{activity.recentTasks.length === 0 ? (
						<div className={styles.empty}>Nenhuma task ainda.</div>
					) : (
						<table className={styles.miniTable}>
							<thead>
								<tr>
									<th>Quando</th>
									<th>Objetivo</th>
									<th>Subtarefas</th>
								</tr>
							</thead>
							<tbody>
								{activity.recentTasks.map((t) => (
									<tr key={t.id}>
										<td className={styles.dim}>{fmtDate(t.createdAt)}</td>
										<td>
											<span className={styles.taskObjective} title={t.objective}>
												{t.objective}
											</span>
										</td>
										<td>
											<span className={styles.taskBadges}>
												<span className={clsx(styles.badge, styles.badgeOk)}>{t.subtasksCompleted}✓</span>
												{t.subtasksFailed > 0 && (
													<span className={clsx(styles.badge, styles.badgeFail)}>{t.subtasksFailed}✗</span>
												)}
												<span className={clsx(styles.badge, styles.badgeNeutral)}>{t.subtasksTotal} total</span>
											</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>

				<div className={styles.tableCard}>
					<h2 className={styles.tableTitle}>
						<Icon path={mdiAlertOutline} size={0.7} style={{ verticalAlign: "middle", marginRight: 6 }} />
						Membros próximos do limite
					</h2>
					{activity.membersNearLimit.length === 0 ? (
						<div className={styles.empty}>Nenhum membro acima de 80% de uso. ✓</div>
					) : (
						<table className={styles.miniTable}>
							<thead>
								<tr>
									<th>Membro</th>
									<th>Uso</th>
								</tr>
							</thead>
							<tbody>
								{activity.membersNearLimit.map((m) => (
									<tr key={m.memberId}>
										<td>{m.memberName}</td>
										<td>
											<div className={styles.pctBar}>
												<div className={styles.pctBarTrack}>
													<div
														className={styles.pctBarFill}
														style={{ width: `${Math.min(100, m.pct)}%` }}
													/>
												</div>
												<span className={styles.pctText}>{m.pct}%</span>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>
			</div>
		</div>
	);
};
